import React, { useState } from "react";
import styles from "./input.module.scss";

import Icon from "../Icon";

import { useField } from "formik";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  placeholder: string;
  name: string;
  label: string | React.ReactElement;
  description?: string;
  money?: boolean;
}

interface SearchProps extends Omit<InputProps, "label"> {
  label?: string;
  icon?: React.ReactElement;
}

const Input: React.FC<InputProps> = ({ ...props }) => {
  const [field, meta, helpers] = useField(props);

  const isError = Boolean(meta.error && meta.touched);

  const [toggleShowPassword, setToggleShowPassword] = useState(false);

  const [active, setActive] = useState(false);

  const inputType =
    props.type !== "password"
      ? props.type
      : toggleShowPassword
        ? "text"
        : "password";

  const handleClearIcon = () => {
    helpers.setValue("");
  };

  const handleToggleShowPassword = () => {
    setToggleShowPassword(!toggleShowPassword);
  };

  // handle number formatting
  const formatNumber = (value: string | number) => {
    if (!value) return "";
    return new Intl.NumberFormat().format(Number(value));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, "");
    if (!/^\d*$/.test(rawValue)) return; // prevent non-digits
    helpers.setValue(rawValue); // store raw number
  };

  const textIcon = active && (
    <button
      type="button"
      className={styles["input__icon"]}
      onClick={handleClearIcon}
    >
      {props.type === "datetime-local" || props.type === "date" ? (
        ""
      ) : (
        <Icon iconName="close" />
      )}
    </button>
  );

  const passwordIcon = (
    <button
      type="button"
      aria-label="password"
      className={styles["input__icon"]}
      onClick={handleToggleShowPassword}
    >
      <Icon iconName={toggleShowPassword ? "eyeOpen" : "eyeClose"} />
    </button>
  );

  return (
    <div className={styles["input"]}>
      <label htmlFor={props.name}>{props.label}</label>
      <div
        className={styles["input__field"]}
        data-input={props.type === "tel"}
        data-focused={active}
      >
        {props.type === "text" && props.money ? (
          <input
            type="text"
            {...props}
            value={formatNumber(field.value)}
            onChange={handleNumberChange}
            onFocus={() => setActive(true)}
          />
        ) : (
          <input
            {...field}
            {...props}
            type={inputType}
            onFocus={() => setActive(true)}
          />
        )}
        <div className={styles["input__label-icon"]}>
          {props.type === "password"
            ? passwordIcon
            : props.type === "date" || props.type === "time"
              ? ""
              : textIcon}
        </div>
      </div>
      {props.description && (
        <p className={`text-xs text-primary-6 md:text-sm`}>
          {props.description}
        </p>
      )}
      {isError && (
        <p className={styles["input__errors"]} data-error={isError}>
          <span className={styles["input__errors-message"]}>{meta.error}</span>
        </p>
      )}
    </div>
  );
};

// Reusable Search Input exported as a named export
const SearchInput: React.FC<SearchProps> = ({ ...props }) => {
  return (
    <div className={styles["search-input"]}>
      {props.icon}
      <input
        type="text"
        {...props}
        placeholder={props.placeholder}
        name={props.name}
        onChange={props.onChange}
        autoComplete="off"
      />
    </div>
  );
};

export { SearchInput };
export default Input;
